import { useEffect } from "react";
import {
  createSearchParams,
  Link as RouterLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { Link, Typography } from "@mui/material";
import { ErrorBoundary } from "~/components/error-boundary";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { useViewer } from "~/providers/viewer";
import { TokenProvider } from "~/providers/token";
import {
  IntegrationsRoutes,
  InventoryRoutes,
  PageLayout,
  Redirect,
  SecurityModelRoutes,
  SettingsRoutes,
  VulnsRoutes,
} from "~/routes";
import {
  LoadViewerDocument,
  useGetIntegrationsSummaryQuery,
} from "~/operations";
import { CicdRoutes } from "~/routes/space/cicd-routes";
import { AppNavDrawerItem, useAppNavDrawer } from "~/providers/app-nav-drawer";
import { ComplianceRoutes } from "~/routes/space/compliance-routes";
import { TicketsRoutes } from "~/routes/space/tickets-routes";
import { WorkspaceRoutes } from "~/routes/space/workspace-routes";
import { isSpaceOrWorkspaceScope, useScope } from "~/hooks/useScope";
import { ReportingRoutes } from "~/routes/space/reporting-routes";
import TicketEntryPage from "~/entry-pages/space/tickets/ticket/TicketEntryPage";
import { AssetSoftwareDetails } from "~/pages/inventory/components/AssetSoftwareDetails";
import {
  AttachIcon,
  CicdIcon,
  ComplianceIcon,
  FleetIcon,
  HomeIcon,
  IntegrationsIcon,
  ReportingIcon,
  SecurityModelIcon,
  SecurityPipelineNavIcon,
  SettingsIcon,
  VulnerabilitiesIcon,
  WorkspacesIcon,
} from "~/icons";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { FullTextSearchResults } from "~/pages/Search";
import { DashboardRoutes } from "~/routes/space/dashboard-routes";
import { FindingsRoutes } from "~/routes/space/findings-routes";
import { SpaceDashboardPage } from "~/pages/space/SpaceDashboardPage/SpaceDashboardPage";
import { useSecurityPipelineIntegrations } from "~/pages/integrations/security-pipeline/hooks/useSecurityPipelineIntegrations";

import { useCanListCases } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/ListCases";
import { useCanGetWorkspace } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/GetWorkspace";
import { useCanCreate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Create";
import { useSpaceSettingsPermissions } from "~/hooks/useSpaceSettingsPermissions";
import { useCanListAssets } from "~/lib/iam/actions/mondoo/policy/AssetStore/ListAssets";

export function SpaceRoutes() {
  const [searchParams, _setSearchParams] = useSearchParams();
  const location = useLocation();
  const { viewerSettings, updateViewerSettings } = useViewer();
  const { setTree } = useAppNavDrawer();
  const { isFeatureEnabled } = useFeatureFlags();
  const spaceId = searchParams.get("spaceId");
  const workspaceMrn = searchParams.get("workspaceId");
  const isWorkspace = !!workspaceMrn;
  const isSpace = !isWorkspace;

  const { activeScope: scope, activeResult, spaceScope } = useScope();
  const space = spaceScope;
  const spaceMrn = spaceScope?.mrn;

  const { data: integrationSummaryData, loading: integrationSummaryLoading } =
    useGetIntegrationsSummaryQuery({
      variables: { input: { spaceMrn: spaceMrn || "" } },
      skip: !spaceMrn,
    });

  const { integration: securityPipelineIntegration } =
    useSecurityPipelineIntegrations();

  const integrationsSummary = integrationSummaryData?.integrationsSummary;
  const integrationCount = integrationsSummary?.total ?? 0;
  const hasIntegrations = integrationCount > 0;
  // loop through the integrations and check if any at all are failing, return true or false
  const hasFailingIntegrations = Boolean(
    integrationsSummary?.integrationSummaries?.some(
      ({ failedCount, warningCount }) => failedCount > 0 || warningCount > 0,
    ),
  );

  const canCreateIntegration = useCanCreate(spaceScope);
  const canListTickets = useCanListCases(spaceScope);
  const canGetWorkspace = useCanGetWorkspace(scope);
  const { canViewSpaceSettings } = useSpaceSettingsPermissions(spaceScope);
  const hasSettingsAccess =
    (isSpace && canViewSpaceSettings) || (isWorkspace && canGetWorkspace);

  const canAccessSpaceData = useCanListAssets(spaceScope);
  const hasSpaceAccess = isSpace && canAccessSpaceData;
  const hasWorkspaceAccess = isWorkspace && canGetWorkspace;

  // TODO: add "last_workspace_id"?
  useEffect(() => {
    // If user's current spaceId is different from last spaceId
    if (space && space.id !== viewerSettings?.last_space_id) {
      // Update user's last spaceId
      updateViewerSettings({
        variables: { key: "last_space_id", value: space.id },
        refetchQueries: [LoadViewerDocument],
      });
    }
  }, [space]);

  useEffect(() => {
    const integrationsTree: AppNavDrawerItem["tree"] = [
      {
        text: "All Integrations",
        link: {
          to: `/space/integrations?${scope?.params}`,
        },
        hasError: hasFailingIntegrations,
      },
      canCreateIntegration
        ? {
            text: "Add New Integration",
            link: {
              to: `/space/integrations/add?${scope?.params}`,
            },
          }
        : undefined,
    ].flatMap((x) => x ?? []);

    const findingsTree: AppNavDrawerItem["tree"] = [
      {
        text: "All",
        link: {
          to: `/space/findings/all?${scope?.params}`,
        },
      },
      {
        text: "SLAs",
        link: {
          to: `/space/dashboard/slas?${scope?.params}`,
        },
      },
      {
        text: "Vulnerabilities",
        link: {
          to: `/space/findings/cves?${scope?.params}`,
        },
      },
      {
        text: "Advisories",
        link: {
          to: `/space/findings/advisories?${scope?.params}`,
        },
      },
      {
        text: "Checks",
        link: {
          to: `/space/findings/checks?${scope?.params}`,
        },
      },
      {
        text: "Policies",
        link: {
          to: `/space/findings/policies?${scope?.params}`,
        },
      },
      {
        text: "Exceptions",
        link: {
          to: `/space/findings/exceptions?${scope?.params}`,
        },
      },
    ].flatMap((navItem) => navItem ?? []);

    const inventoryTree: AppNavDrawerItem["tree"] = [
      {
        text: "Assets",
        link: { to: `/space/inventory?${scope?.params}` },
      },
      {
        text: "Software",
        link: {
          to: `/space/inventory/software?${scope?.params}`,
        },
      },
      {
        text: "Exposed assets",
        link: {
          to: `/space/inventory/exposed-assets?${scope?.params}`,
        },
      },
      {
        text: "Query packs",
        link: { to: `/space/inventory/query-packs?${scope?.params}` },
      },
    ];

    setTree(
      [
        hasSpaceAccess || hasWorkspaceAccess
          ? {
              text: "Dashboard",
              icon: <HomeIcon />,
              link: {
                to: `/space/overview?${scope?.params}`,
              },
            }
          : null,
        isSpace && hasSpaceAccess
          ? {
              text: "Workspaces",
              icon: <WorkspacesIcon />,
              link: {
                to: `/space/workspaces?${spaceScope?.params}`,
              },
            }
          : null,
        // Show menu items if user has space access OR if in workspace scope
        hasSpaceAccess || hasWorkspaceAccess
          ? {
              text: "Inventory",
              icon: <FleetIcon />,
              link: {
                to: `/space/inventory?${scope?.params}`,
              },
              tree: inventoryTree,
            }
          : null,
        hasSpaceAccess || hasWorkspaceAccess
          ? {
              text: "Findings",
              icon: <VulnerabilitiesIcon />,
              link: {
                to: `/space/findings?${scope?.params}`,
              },
              tree: findingsTree,
            }
          : null,
        // Only show Compliance, CI/CD, Reporting in space scope (not in workspace scope)
        hasSpaceAccess && isSpace
          ? {
              text: "Compliance",
              icon: <ComplianceIcon />,
              link: {
                to: `/space/compliance?${scope?.params}`,
              },
            }
          : null,
        hasSpaceAccess && isSpace
          ? {
              text: "CI/CD",
              icon: <CicdIcon />,
              link: {
                to: `/space/cicd?${scope?.params}`,
              },
            }
          : null,
        hasSpaceAccess && isSpace
          ? {
              text: "Reporting",
              icon: <ReportingIcon />,
              link: {
                to: `/space/reporting?${scope?.params}`,
              },
            }
          : null,
        (hasSpaceAccess || hasWorkspaceAccess) && canListTickets
          ? {
              text: "Ticketing",
              icon: <AttachIcon />,
              link: {
                to: `/space/tickets?${scope?.params}`,
              },
            }
          : null,
        securityPipelineIntegration &&
        isSpace &&
        isFeatureEnabled("Security Pipeline")
          ? {
              text: "Security Pipeline",
              icon: <SecurityPipelineNavIcon />,
              link: {
                to: `/space/integrations/security_pipeline_github/${securityPipelineIntegration?.mrn.split("/").pop() ?? ""}?${scope?.params}`,
              },
            }
          : null,
        hasSpaceAccess && isSpace
          ? {
              text: "Integrations",
              icon: <IntegrationsIcon />,
              beforeDivider: true,
              link: {
                to:
                  hasIntegrations || !canCreateIntegration
                    ? `/space/integrations?${scope?.params}`
                    : `/space/integrations/add?${scope?.params}`,
              },
              tree: hasIntegrations ? integrationsTree : undefined,
              loading: integrationSummaryLoading,
              selected: location.pathname.startsWith("/space/integrations"),
            }
          : null,
        hasSpaceAccess && isSpace
          ? {
              text: "Security Model",
              icon: <SecurityModelIcon />,
              link: {
                to: `/space/security-model/risk-factors?${scope?.params}`,
              },
              tree: [
                {
                  text: "Risk Factors",
                  link: {
                    to: `/space/security-model/risk-factors?${scope?.params}`,
                  },
                },
                {
                  text: "SLAs",
                  link: {
                    to: `/space/security-model/slas?${scope?.params}`,
                  },
                },
              ],
            }
          : null,
        hasSettingsAccess
          ? {
              text: "Settings",
              icon: <SettingsIcon />,
              link: {
                to: `/space/settings?${scope?.params}`,
              },
            }
          : null,
      ].flatMap((navItem) => navItem ?? []),
    );
    return () => setTree([]);
  }, [
    hasWorkspaceAccess,
    canCreateIntegration,
    canListTickets,
    integrationSummaryLoading,
    integrationCount,
    scope?.params,
    hasSpaceAccess,
    isWorkspace,
    hasIntegrations,
    hasFailingIntegrations,
    location.pathname,
    spaceScope?.params,
    securityPipelineIntegration,
  ]);

  // if we don't have a space id param, try to redirect to the last visited space
  if (!spaceId) {
    const spaceId = viewerSettings?.last_space_id;
    if (spaceId) {
      let newParams: { [key: string]: string | string[] } = {};
      searchParams.forEach((_key: string, value: string) => {
        newParams[value] = searchParams.getAll(value);
      });

      newParams["spaceId"] = spaceId;
      return (
        <Navigate
          replace
          to={window.location.pathname + "?" + createSearchParams(newParams)}
        />
      );
    }
    return <Navigate to="/dashboard" replace />;
  }

  if (!spaceScope || !scope || !isSpaceOrWorkspaceScope(scope) || !space) {
    if (activeResult?.error) {
      const message = activeResult.error.message.includes(
        "PermissionDenied",
      ) ? (
        <Typography>No permissions to view this space.</Typography>
      ) : (
        <></>
      );
      const returnLink = (
        <Link component={RouterLink} to="/dashboard" replace>
          Return to Dashboard
        </Link>
      );
      return (
        <LoadingFailedPage
          what="space"
          message={
            <>
              {message}
              {returnLink}
            </>
          }
        />
      );
    }
    return <LoadingPage what="space" />;
  }

  // Shared workspace: If user tried to access a space route but doesn't have permission,
  // redirect them back to the workspaces list page
  // Exclude workspaces route from redirect to avoid redirect loop
  if (
    isSpace &&
    !hasSpaceAccess &&
    !location.pathname.startsWith("/space/workspaces")
  ) {
    return <Navigate to={`/space/workspaces?spaceId=${spaceId}`} replace />;
  }

  return (
    <TokenProvider space={space}>
      <ErrorBoundary key="space">
        <Routes>
          {/* Integrations Routes */}
          <Route
            path="integrations/*"
            element={<IntegrationsRoutes {...{ scope: space }} />}
          />

          {/* Fleet Routes */}
          {/* Deprecated - Redirect to "/inventory" */}
          <Route
            path="fleet/*"
            element={<Redirect from="space/fleet" to="space/inventory" />}
          />

          <Route element={<PageLayout />}>
            <Route
              index
              element={<SpaceDashboardPage space={space} scope={scope} />}
            />

            <Route
              path="workspaces/*"
              element={<WorkspaceRoutes {...{ space }} />}
            />

            {/* Overview Route */}
            <Route
              path="overview"
              element={<SpaceDashboardPage space={space} scope={scope} />}
            />

            {/* Dashboard Routes */}
            <Route
              path="dashboard/*"
              element={<DashboardRoutes space={space} scope={scope} />}
            />

            {/* Settings Routes */}
            <Route
              path="settings/*"
              element={<SettingsRoutes {...{ space, scope }} />}
            />

            {/* Security Models Routes */}
            <Route
              path="security-model/*"
              element={<SecurityModelRoutes {...{ space }} />}
            />

            {/* CI/CD Projects Routes */}
            <Route path="cicd/*" element={<CicdRoutes {...{ space }} />} />

            {/* Reporting Routes */}
            <Route
              path="reporting/*"
              element={<ReportingRoutes {...{ space }} />}
            />

            <Route path="software">
              <Route
                path=":name/*"
                element={<AssetSoftwareDetails scope={scope} />}
              />
            </Route>

            {canListTickets && (
              <>
                <Route
                  path="cases/*"
                  element={<TicketsRoutes {...{ space, scope }} />}
                />
                <Route
                  path="tickets/*"
                  element={<TicketsRoutes {...{ space, scope }} />}
                />
              </>
            )}
            <Route
              path="case"
              element={<TicketEntryPage space={space} scope={scope} />}
            />
            <Route
              path="ticket"
              element={<TicketEntryPage space={space} scope={scope} />}
            />
          </Route>
          <Route element={<PageLayout maxWidth={false} />}>
            <Route
              path="search"
              element={<FullTextSearchResults scope={scope} />}
            />
          </Route>
          {/* Compliance Routes */}
          <Route
            path="compliance/*"
            element={<ComplianceRoutes {...{ space }} />}
          />
          {/* Inventory Routes */}
          <Route
            path="inventory/*"
            element={<InventoryRoutes {...{ scope, spaceScope, space }} />}
          />
          {/* Vulnerabilities Routes */}
          <Route
            path="vulns/*"
            element={<VulnsRoutes {...{ space, scope }} />}
          />
          {/* New Vulnerabilities Routes */}
          <Route
            path="findings/*"
            element={<FindingsRoutes spaceScope={spaceScope} scope={scope} />}
          />
        </Routes>
      </ErrorBoundary>
    </TokenProvider>
  );
}
